import { Component } from '@angular/core';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GroupDescriptor, CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import { NGXLogger } from 'ngx-logger';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { TermAndConditionService } from 'src/app/core/service/term-and-condition.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { TermAndConditionEdit } from '../model/term-and-condition';

@Component({
  selector: 'app-term-and-condition-list',
  templateUrl: './term-and-condition-list.component.html',
  styleUrls: ['./term-and-condition-list.component.scss']
})
export class TermAndConditionListComponent
{
  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public deleteDialogOpened = false;
  public enableAdvanced = false;
  public count: number;
  public isNew = false;
  public editDataItem: TermAndConditionEdit;
  public selectedTermAndCondition: TermAndConditionEdit;
  public filterText = '';

  /**
   * Constructor
   */
  constructor(
    private termAndConditionService: TermAndConditionService,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService
  )
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.loadGridData();
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const termAndConditionId = this.selectedTermAndCondition.id;
      this.termAndConditionService.deleteCommunityServiceCategory(termAndConditionId).subscribe({
        next: () =>
        {
          this.view.data = this.view.data.filter(e => e.id !== termAndConditionId);
          this.view.refreshView();
          this.count = this.view.data.length;
          this.messageHandlerService.displaySuccess('TERM-AND-CONDITION-DELETED-SUCCESSFULLY');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedTermAndCondition = null;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    this.termAndConditionService.getTermAndConditionById(id).subscribe({
      next: (data) =>
      {
        this.selectedTermAndCondition = data as TermAndConditionEdit;
        this.open('deleteDialog');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public addHandler(): void
  {
    this.isNew = true;
    setTimeout(() =>
    {
      this.editDataItem = new TermAndConditionEdit();
    });
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.termAndConditionService.getTermAndConditionById(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public saveChangesHandler(termAndCondition: TermAndConditionEdit): void
  {
    this.termAndConditionService.saveTermAndCondition(termAndCondition, this.isNew, termAndCondition.id).subscribe({
      next: () =>
      {
        this.logger.info('saved', termAndCondition);

        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('TERM-AND-CONDITION-SAVED-SUCCESSFULLY');

        this.loadGridData();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  private loadGridData(): void
  {
    this.view.startLoading();
    this.termAndConditionService.getTermsAndConditionsList().subscribe({
      next: (data) =>
      {
        data.forEach(d => { d.lastModifiedDate = new Date(d.lastModifiedDate); });
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'name', dir: 'asc' }]);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }
}
